import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useState, FormEvent, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import authApi from "Service/Auth.api";
import { RouteUrls } from "Constants/RouteUrls";
import { FormDataType } from "DataTypes/Form";
import { motion } from "framer-motion";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ImgPath from "Constants/Imgs";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const ResetPassword = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataType[]>([
    {
      id: "email",
      label: "Email Address",
      value: email || "",
      boxType: "input-box",
      validatetype: "email",
      type: "email",
      readOnly: true,
      required: true,
    },
    {
      id: "newPassword",
      label: "New Password",
      placeholder: "Enter new password",
      value: "",
      boxType: "input-box",
      validatetype: "password",
      type: "password",
      required: true,
    },
  ]);

  // On mount, prefill email
  useEffect(() => {
    if (email) {
      setFormData((prev) =>
        prev.map((f) => (f.id === "email" ? { ...f, value: email } : f))
      );
    }
  }, [email]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleChange = (id: string, value: string) => {
    setFormData((prev) =>
      prev.map((field) =>
        field.id === id
          ? { ...field, value, error: false, errorText: "" }
          : field
      )
    );
  };

  const validate = () => {
    let valid = true;
    setFormData((prev) =>
      prev.map((field) => {
        if (field.required && !field.value) {
          valid = false;
          return {
            ...field,
            error: true,
            errorText: `${field.label} is required`,
          };
        }
        if (field.id === "newpassword" && field.value) {
          valid = false;
          return {
            ...field,
            error: true,
            errorText: `${field.label} is required`,
          };
        }
        return { ...field };
      })
    );
    return valid;
  };

  const handleSubmit = async () => {
    setError("");
    if (!validate()) return;

    const payload = {
      email: formData.find((f) => f.id === "email")?.value,
      token, // new addition
      newPassword: formData.find((f) => f.id === "newPassword")?.value,
    };
    setLoading(true);
    try {
      const res = await authApi.resetPassword(payload);
      if (res?.success) {
        navigate(`/${RouteUrls.login}`);
      } else {
        setError(res?.message || "Failed to send reset link.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: "100vh", overflow: "auto" }}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            mt: 1,
            mb: 1,
            color: "primary.main",
          }}
        >
          <Box
            component="img"
            src={"https://nolegalpaisa.com/assets/images/new_logo.webp"}
            alt="Logo"
            sx={{
              width: { xs: "150px", sm: "200px", md: "230px" },
              height: { xs: "60px", sm: "80px", md: "83px" },
              objectFit: "contain",
              cursor: "pointer",
              filter: "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.3))",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
            onClick={() => (window.location.href = "/home.html")}
          />
        </Typography>
      </Box>
      <Grid
        container
        component="main"
        sx={{
          height: { xs: "auto", sm: "80vh" },
          minHeight: { xs: "70vh", sm: "80vh" },
          py: { xs: 2, sm: 0 },
        }}
        alignItems="center"
        justifyContent="center"
      >
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 8,
            lg: 6,
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            // style={{ width: "100%" }}
          >
            <Paper
              elevation={6}
              square
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: { xs: "auto", sm: "100%" },
                width: "100%",
                maxWidth: { xs: "100%", sm: "450px", md: "500px" },
                minHeight: { xs: "auto", sm: "400px" },
                py: { xs: 3, sm: 2 },
              }}
            >
              <Box
                sx={{
                  mx: { xs: 2, sm: 3, md: 4 },
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "400px" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  overflow: "visible",
                }}
              >
                <Avatar
                  sx={{
                    m: 1,
                    bgcolor: "primary.main",
                    width: { xs: 40, sm: 56 },
                    height: { xs: 40, sm: 56 },
                  }}
                >
                  <LockResetIcon
                    sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
                  />
                </Avatar>
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                    textAlign: "center",
                  }}
                >
                  Reset Password
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    textAlign: "center",
                    color: "text.primary",
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  Enter a new password for your account.
                </Typography>

                <Box component="form" noValidate sx={{ mt: 1, width: "100%" }}>
                  {formData.map((field) => (
                    <TextField
                      key={field.id}
                      fullWidth
                      label={field.label}
                      variant="outlined"
                      margin="normal"
                      required={field.required}
                      type={
                        field.id === "newPassword"
                          ? showPassword
                            ? "text"
                            : "password"
                          : field.type
                      }
                      value={field.value as string}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!field.error}
                      helperText={field.errorText}
                      InputProps={{
                        readOnly: field.readOnly,
                        endAdornment:
                          field.id === "newPassword" ? (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePasswordVisibility}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                  ))}

                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{
                      mt: 3,
                      py: { xs: 1.5, sm: 1.75 },
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>

                  {error && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{
                        mt: 1,
                        textAlign: "center",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      {error}
                    </Typography>
                  )}

                  <Typography
                    sx={{
                      textAlign: "center",
                      textDecoration: "underline",
                      cursor: "pointer",
                      mt: 2,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                    variant="body2"
                    onClick={() => navigate(`/${RouteUrls.login}`)}
                  >
                    Back to Login
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPassword;
